<template>
  <el-dialog
    title="风险处理"
    :visible.sync="dialogVisible"
    width="1200px"
    :close-on-click-modal="false"
    append-to-body
    class="dialog"
    @close="onClose"
    @open="onOpen"
  >
    <div class="flex col-top">
      <div class="left">
        <div class="card">
          <div class="section">
            <div class="section-title">
              <base-section-title title="风险详情" />
            </div>
            <div class="section-content">
              <div class="m-b-6">风险描述：{{ detailInfo.riskContent }}</div>
              <el-descriptions size="medium" :column="1" border>
                <el-descriptions-item
                  label="风险涉及对象"
                  :label-style="{ width: '200px' }"
                >
                  <look-text-more
                    :maxlength="200"
                    :content="detailInfo.triggerValues"
                  />
                </el-descriptions-item>
                <el-descriptions-item
                  label="命中风险详情"
                  :label-style="{ width: '200px' }"
                >
                  <look-text-more :maxlength="300" :content="detailInfo.riskDesc" />
                </el-descriptions-item>
              </el-descriptions>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="section">
            <div class="section-title">
              <base-section-title title="处理风险" />
            </div>
            <div class="section-content">
              <el-form
                ref="form"
                :model="formData"
                :rules="formDataRule"
                label-suffix="："
                size="small"
                label-width="140px"
                style="width: 100%"
              >
                <el-form-item label="是否确认风险" prop="processStatus">
                  <span slot="label">
                    是否确认风险
                    <el-tooltip effect="dark" content="对系统判定的风险进行人工审核，以确认是否为实际业务中产生的风险" placement="top-start">
                      <i class="el-icon-question"></i>
                    </el-tooltip>
                    :
                  </span>
                  <el-radio-group v-model="formData.processStatus">
                    <el-radio
                      v-for="item in riskHandleList"
                      :key="item.value"
                      :label="item.value"
                    >
                      {{ item.radioLabel }}
                    </el-radio>
                  </el-radio-group>
                </el-form-item>

                <el-form-item label="处理说明" prop="handleReason">
                  <el-input
                    v-model.trim="formData.handleReason"
                    type="textarea"
                    :rows="3"
                    placeholder="请输入处理备注，最多500个字"
                    clearable
                    maxlength="500"
                    show-word-limit
                  />
                </el-form-item>

                <el-form-item label="处理人">
                  <span>{{ employeeName }}</span>
                </el-form-item>
                <el-form-item label="处理人所属企业">
                  <span>{{ companyName }}</span>
                </el-form-item>

                <el-form-item label="佐证材料">
                  <upload
                    ref="uploadFile"
                    :file-id-before.sync="formData.files"
                    :props-data="{
                      uploadMode: 'fileId',
                      limit: 10,
                      fileSize: 5
                    }"
                  >
                    <span slot="tip">
                      不超过5M,支持扩展名：.rar .zip .doc .docx .pdf .jpg
                    </span>
                  </upload>
                </el-form-item>
              </el-form>
            </div>
          </div>
        </div>
      </div>

      <el-divider direction="vertical" />

      <risk-company-list class="flex-1" :order-monitor-id="monitorId" />
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button class="medium-button" size="small" @click="closeDialog">
        取消
      </el-button>
      <el-button
        type="primary"
        class="medium-button"
        size="small"
        @click="confirm"
      >
        确认
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
// 组件
import Upload from '@/components/common/Upload'
import BaseSectionTitle from '@/components/common/BaseSectionTitle'
import LookTextMore from '@/components/risk/LookTextMore'
import RiskCompanyList from '@/components/risk/RiskCompanyList'

// 数据字典
import { riskHandleStatusDict, riskPointsDict, riskWhetherDict } from '@/dictionary/risk'

// 后端api
import { setRiskHandleApi, getRiskHandleDetailApi } from '@/api/risk/manage'

import { mapState } from 'vuex'

export default {
  name: 'RiskHandleFormDialog',
  components: {
    Upload,
    BaseSectionTitle,
    LookTextMore,
    RiskCompanyList
  },
  model: {
    prop: 'visible',
    event: 'change'
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /**
     * 操作行数据
     */
    rowData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      // 字典
      riskHandleList: [],
      riskWhetherDict,

      // 详情
      detailInfo: {},

      formData: {
        processStatus: '',
        handleReason: '',
        files: []
      },
      formDataRule: {
        processStatus: {
          required: true,
          message: '请选择处理结果',
          trigger: ['change', 'blur']
        },
        handleReason: {
          required: true,
          message: '请输入处理说明',
          trigger: ['change', 'blur']
        }
      }
    }
  },
  computed: {
    ...mapState('user', ['employeeName']),
    ...mapState('company', ['companyName']),

    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('change', val)
      }
    },

    monitorId() {
      return this.rowData?.monitorId || ''
    }
  },

  mounted() {
    this.riskHandleList = riskHandleStatusDict.array.slice(1, 3)
  },

  methods: {
    onOpen() {
      this.$nextTick(() => {
        this.getDetail()
      })
    },

    /**
     * 获取详情
     */
    getDetail() {
      getRiskHandleDetailApi(this.rowData.monitorId)
        .then(res => {
          const data = res?.data || {}
          const descObj = riskPointsDict.translate(data?.monitorInfo?.riskCode)
          this.detailInfo = {
            ...data,
            triggerValues: (data?.riskTriggers || [])
              .map(item => item.triggerValue)
              .join(','),
            riskDesc: descObj.getContent
              ? descObj.getContent(data?.monitorInfo?.orderName, data?.riskTriggers || [], data?.monitorInfo?.monitorValue) || ''
              : ''
          }
        })
        .catch(err => {
          this.$message.error(err.message)
        })
    },

    async confirm() {
      const valid = await this.$refs.form.validate()
      if (!valid) return

      this.$confirm(`确定提交？`, '提示', {
        type: 'warning'
      }).then(() => {
        setRiskHandleApi({
          orderMonitorId: this.rowData.monitorId,
          ...this.formData
        })
          .then(() => {
            this.$message.success('操作成功')
            this.closeDialog()
            this.$emit('complete')
          })
          .catch(err => {
            this.$message.error(err.message || '操作失败')
          })
      })
    },

    /**
     * 关闭回调，重置数据
     */
    onClose() {
      this.$refs && this.$refs.form.resetFields()
      this.formData.files = []
    },

    closeDialog() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
.left {
  flex: 0 0 650px;
  width: 650px;
}
::v-deep .el-divider--vertical {
  height: 500px;
  flex: 0 0 1px;
  background-color: $info-fourth-color;
}
</style>
